import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import elegantSmoke from '../assets/elegant-smoke-wallpaper-background-dark-design.webp';
import { usePerfMode } from '../hooks/usePerfMode';
import { hideOnMobile, pauseAnimationsOnMobile, respectReducedMotion } from '../styles/performance';

const ACCENT = '#b388ff';
const ACCENT_DIM = 'rgba(179,136,255,0.35)';

/* ─── Keyframes ─── */
const orbDrift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(30px, -20px) scale(1.05); }
  66%       { transform: translate(-20px, 15px) scale(0.97); }
`;

const auroraPulse = keyframes`
  0%, 100% { opacity: 0.45; }
  50%       { opacity: 0.65; }
`;

const gridScroll = keyframes`
  0%   { background-position: 0 0; }
  100% { background-position: 0 60px; }
`;

const revealClip = keyframes`
  from { clip-path: inset(0 100% 0 0); opacity: 0; transform: translateY(40px); }
  to   { clip-path: inset(0 0 0 0);   opacity: 1; transform: translateY(0); }
`;

const letterReveal = keyframes`
  from { opacity: 0; transform: translateY(80%) rotateX(-40deg); filter: blur(4px); }
  to   { opacity: 1; transform: translateY(0) rotateX(0);       filter: blur(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const orbitSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.6; }
  100% { transform: scale(1.6); opacity: 0; }
`;

const floatBadge = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-8px); }
`;

const shineSweep = keyframes`
  0%   { background-position: -150% 0; }
  100% { background-position: 250% 0; }
`;

const pulseScale = keyframes`
  0%, 100% { transform: scale(1);   filter: drop-shadow(0 0 16px rgba(179,136,255,0.35)); }
  50%      { transform: scale(1.04); filter: drop-shadow(0 0 28px rgba(179,136,255,0.55)); }
`;

const confettiFloat = keyframes`
  0%   { transform: translateY(0) rotate(0deg); opacity: 0; }
  10%  { opacity: 0.9; }
  50%  { transform: translateY(-16px) rotate(8deg); }
  100% { transform: translateY(-32px) rotate(0deg); opacity: 0; }
`;

const scrollBounce = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.5; }
  50%       { transform: translateY(8px); opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const lineGrow = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const particleDrift = keyframes`
  0%   { transform: translateY(0) translateX(0); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 0.6; }
  100% { transform: translateY(-100vh) translateX(var(--dx)); opacity: 0; }
`;

/* ─── Layout ─── */
const HeroContainer = styled.section`
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  color: #fff;
  isolation: isolate;
`;

const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;
  background: url(${elegantSmoke}) center / cover no-repeat;
  transform: scale(1.08);
  transition: transform 0.1s linear;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(5, 3, 15, 0.92) 0%,
      rgba(10, 6, 25, 0.75) 40%,
      rgba(5, 3, 15, 0.88) 100%
    );
  }
`;

const AuroraLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, ${({ $color }) => $color} 0%, transparent 70%);
  filter: blur(80px);
  animation: ${orbDrift} ${({ $duration }) => $duration}s ease-in-out infinite,
             ${auroraPulse} ${({ $duration }) => $duration * 1.3}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  will-change: transform, opacity;
  transition: transform 0.15s ease-out;
  ${pauseAnimationsOnMobile}
  ${respectReducedMotion}
`;

const GridFloor = styled.div`
  position: absolute;
  bottom: 0;
  left: -50%;
  right: -50%;
  height: 55%;
  background-image:
    linear-gradient(rgba(179,136,255,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(179,136,255,0.07) 1px, transparent 1px);
  background-size: 60px 60px;
  transform: perspective(500px) rotateX(65deg);
  transform-origin: center top;
  mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
  animation: ${gridScroll} 4s linear infinite;
  pointer-events: none;
  ${pauseAnimationsOnMobile}
  ${respectReducedMotion}
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.55) 100%);
  pointer-events: none;
`;

const Noise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  pointer-events: none;
  ${hideOnMobile}
`;

const Particle = styled.span`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $bright }) => $bright ? ACCENT : 'rgba(255,255,255,0.6)'};
  left: ${({ $x }) => $x}%;
  bottom: -10px;
  --dx: ${({ $dx }) => $dx}px;
  animation: ${particleDrift} ${({ $dur }) => $dur}s linear infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  box-shadow: 0 0 ${({ $size }) => $size * 2}px ${ACCENT_DIM};
  pointer-events: none;
`;

/* ─── Content ─── */
const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 10rem 6rem 5rem;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  align-items: center;
  gap: 3rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    padding: 7rem 2.5rem 4rem;
    gap: 2.5rem;
  }
  @media (max-width: 768px) {
    padding: 6.5rem 1.25rem 5rem;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards 0.2s;

  span {
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.75);
  }
`;

const EyebrowLine = styled.div`
  width: 48px;
  height: 2px;
  background: linear-gradient(90deg, ${ACCENT}, transparent);
  transform-origin: left;
  animation: ${lineGrow} 0.8s ease forwards 0.5s;
  transform: scaleX(0);
`;

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  margin-bottom: 1.75rem;
  perspective: 1200px;
`;

const WordRow = styled.h1`
  font-size: clamp(3.2rem, 8vw, 7rem);
  font-weight: 900;
  line-height: 0.95;
  margin: 0;
  letter-spacing: 0.04em;
  display: flex;
  transform: translateX(${({ $offset }) => $offset}px);
  opacity: 0;
  animation: ${revealClip} 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay}s;

  @media (max-width: 768px) {
    transform: none;
  }
`;

const Letter = styled.span`
  display: inline-block;
  opacity: 0;
  animation: ${letterReveal} 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay}s;
  background: linear-gradient(
    120deg,
    #ffffff 0%,
    ${ACCENT} 40%,
    #ffffff 70%,
    ${ACCENT} 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: default;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
              filter 0.35s ease;

  &:hover {
    transform: translateY(-6px) scale(1.08);
    filter: drop-shadow(0 0 12px rgba(179,136,255,0.7));
    animation: ${shimmer} 1.2s linear;
  }
`;

const Subtitle = styled.p`
  font-size: clamp(1rem, 2vw, 1.35rem);
  margin: 0;
  max-width: 560px;
  line-height: 1.6;
  color: rgba(220, 220, 240, 0.9);
  padding-left: 1.25rem;
  border-left: 2px solid ${ACCENT};
  opacity: 0;
  animation: ${fadeIn} 0.9s ease forwards 1.4s;
`;

const CorporateIntro = styled.p`
  font-size: clamp(0.9rem, 1.5vw, 1.05rem);
  margin: 1.25rem 0 0;
  max-width: 580px;
  line-height: 1.7;
  color: rgba(200, 200, 220, 0.75);
  opacity: 0;
  animation: ${fadeIn} 0.9s ease forwards 1.7s;
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  opacity: 0;
  animation: ${fadeIn} 0.9s ease forwards 2s;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  cursor: pointer;

  ${({ $primary }) => $primary ? css`
    background: linear-gradient(135deg, ${ACCENT}, #7c3aed);
    color: #fff;
    box-shadow: 0 4px 24px rgba(179,136,255,0.35);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 32px rgba(179,136,255,0.5);
    }
  ` : css`
    background: rgba(255,255,255,0.05);
    color: #fff;
    border: 1px solid rgba(179,136,255,0.35);
    backdrop-filter: blur(8px);

    &:hover {
      transform: translateY(-3px);
      border-color: ${ACCENT};
      box-shadow: 0 0 20px rgba(179,136,255,0.2);
    }
  `}
`;

const StatsRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(179,136,255,0.15);
  opacity: 0;
  animation: ${fadeIn} 0.9s ease forwards 2.3s;

  @media (max-width: 480px) {
    gap: 1.25rem;
  }
`;

const Stat = styled.div`
  .value {
    font-size: 1.5rem;
    font-weight: 800;
    background: linear-gradient(120deg, #fff, ${ACCENT});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    line-height: 1;
  }
  .label {
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(200,200,220,0.6);
    margin-top: 0.35rem;
  }
`;

/* ─── Anniversary Badge ─── */
const RightCol = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  opacity: 0;
  animation: ${fadeIn} 1s ease forwards 0.8s;

  @media (max-width: 1200px) {
    justify-content: flex-start;
    order: -1;
  }
`;

const BadgeOuter = styled.div`
  position: relative;
  animation: ${floatBadge} 5s ease-in-out infinite;
`;

const OrbitRing = styled.div`
  position: absolute;
  inset: -24px;
  border-radius: 50%;
  border: 1px dashed rgba(179,136,255,0.25);
  animation: ${orbitSpin} 20s linear infinite;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${ACCENT};
    box-shadow: 0 0 12px ${ACCENT};
    transform: translateX(-50%);
  }
`;

const PulseRing = styled.div`
  position: absolute;
  inset: -8px;
  border-radius: 24px;
  border: 1px solid rgba(179,136,255,0.3);
  animation: ${pulseRing} 3s ease-out infinite;
`;

const AnniversaryBadge = styled.div`
  position: relative;
  padding: 2rem 2.75rem;
  border-radius: 28px;
  background: radial-gradient(140% 140% at 10% 10%, rgba(179,136,255,0.2), rgba(12,10,24,0.6));
  border: 1px solid rgba(179,136,255,0.4);
  box-shadow:
    0 20px 60px rgba(179,136,255,0.15),
    inset 0 0 0 1px rgba(255,255,255,0.06);
  backdrop-filter: blur(16px);
  transform-style: preserve-3d;
  transition: transform 0.4s ease, box-shadow 0.4s ease;

  &:hover {
    transform: perspective(800px) rotateY(-6deg) rotateX(4deg) scale(1.02);
    box-shadow:
      0 30px 80px rgba(179,136,255,0.25),
      inset 0 0 0 1px rgba(255,255,255,0.1);
  }
`;

const Confetti = styled.div`
  position: absolute;
  inset: -10px;
  pointer-events: none;
  overflow: hidden;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 180%;
    height: 180%;
    left: -40%;
    top: -30%;
    background-image:
      radial-gradient(2px 2px at 10% 15%, rgba(255,255,255,0.9) 60%, transparent 61%),
      radial-gradient(2px 2px at 30% 45%, rgba(179,136,255,0.9) 60%, transparent 61%),
      radial-gradient(2px 2px at 55% 25%, rgba(139,92,246,0.9) 60%, transparent 61%),
      radial-gradient(2px 2px at 75% 65%, rgba(255,150,255,0.8) 60%, transparent 61%);
    animation: ${confettiFloat} 3.8s ease-in-out infinite;
    opacity: 0.55;
  }
  &::after {
    animation-duration: 4.6s;
    opacity: 0.4;
  }
`;

const BadgeTop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  .pill {
    padding: 8px 18px;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 999px;
    background: rgba(179,136,255,0.15);
    border: 1px solid rgba(179,136,255,0.3);
    color: rgba(255,255,255,0.95);
    letter-spacing: 0.04em;
  }
`;

const BigYears = styled.div`
  display: flex;
  align-items: baseline;
  gap: 18px;
  line-height: 0.9;

  .num {
    font-size: clamp(5.5rem, 13vw, 9.5rem);
    font-weight: 900;
    letter-spacing: 1px;
    background: linear-gradient(120deg, #ffffff, ${ACCENT} 30%, #ffffff 60%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-size: 200% 100%;
    animation: ${shineSweep} 2.8s linear infinite, ${pulseScale} 2.4s ease-in-out infinite;
  }
  .label {
    font-size: clamp(1.35rem, 3.2vw, 2.4rem);
    font-weight: 800;
    letter-spacing: 0.28em;
    color: rgba(255,255,255,0.95);
    text-transform: uppercase;
  }
`;

const BadgeTagline = styled.div`
  margin-top: 10px;
  font-size: 1.05rem;
  color: rgba(200,200,220,0.8);
  letter-spacing: 0.06em;
  text-align: center;
  line-height: 1.5;
  max-width: 280px;
`;

const ScrollIndicator = styled.a`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: rgba(255,255,255,0.45);
  font-size: 0.7rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  opacity: 0;
  animation: ${fadeIn} 1s ease forwards 2.6s;
  transition: color 0.2s ease;

  &:hover { color: ${ACCENT}; }

  .chevron {
    width: 20px;
    height: 20px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
    animation: ${scrollBounce} 2s ease-in-out infinite;
  }
`;

/* ─── Helpers ─── */
const WORDS = [
  { text: 'SOLVE',   offset: 0,   baseDelay: 0.4 },
  { text: 'RESOLVE', offset: 48,  baseDelay: 0.65 },
  { text: 'EVOLVE',  offset: 96,  baseDelay: 0.9 },
];

const ORBS = [
  { top: '10%', left: '5%',  size: 420, color: 'rgba(139,92,246,0.5)',  duration: 14, delay: 0 },
  { top: '60%', left: '70%', size: 360, color: 'rgba(179,136,255,0.4)', duration: 18, delay: 2 },
  { top: '30%', left: '55%', size: 280, color: 'rgba(99,60,180,0.35)',  duration: 12, delay: 4 },
];

const SplitWord = ({ word, baseDelay }) => (
  <>
    {word.split('').map((ch, i) => (
      <Letter key={i} $delay={baseDelay + i * 0.06}>
        {ch}
      </Letter>
    ))}
  </>
);

const HeroSection = () => {
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const pendingRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [bgShift, setBgShift] = useState({ x: 0, y: 0 });
  const { lite, coarsePointer } = usePerfMode();

  const particles = useMemo(
    () =>
      Array.from({ length: lite ? 0 : 12 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        size: Math.random() * 2.5 + 1,
        dur: Math.random() * 12 + 10,
        delay: Math.random() * 10,
        dx: (Math.random() - 0.5) * 80,
        bright: Math.random() > 0.7,
      })),
    [lite]
  );

  const handleMouseMove = useCallback((e) => {
    pendingRef.current = e;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const evt = pendingRef.current;
      if (!evt) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = (evt.clientX - rect.left) / rect.width - 0.5;
      const y = (evt.clientY - rect.top) / rect.height - 0.5;
      setMouse({ x, y });
      setBgShift({ x: x * 20, y: y * 12 });
    });
  }, []);

  useEffect(() => {
    if (coarsePointer) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    el.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleMouseMove, coarsePointer]);

  const visibleOrbs = lite ? ORBS.slice(0, 1) : ORBS;

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeroContainer id="home" ref={containerRef}>
      <BackgroundImage style={{ transform: `scale(1.08) translate(${bgShift.x * 0.3}px, ${bgShift.y * 0.3}px)` }} />
      <AuroraLayer>
        {visibleOrbs.map((o, i) => (
          <Orb
            key={i}
            $color={o.color}
            $duration={o.duration}
            $delay={o.delay}
            style={{
              top: o.top,
              left: o.left,
              width: o.size,
              height: o.size,
              transform: `translate(${mouse.x * (30 + i * 10)}px, ${mouse.y * (20 + i * 8)}px)`,
            }}
          />
        ))}
      </AuroraLayer>
      <GridFloor />
      <Noise />
      <Vignette />
      {particles.map((p) => (
        <Particle key={p.id} {...{ $x: p.x, $size: p.size, $dur: p.dur, $delay: p.delay, $dx: p.dx, $bright: p.bright }} />
      ))}
      <HeroContent>
        <LeftCol>
          <Eyebrow>
            <EyebrowLine />
            <span>Solve your entity to the right quantity</span>
          </Eyebrow>

          <HeadingGroup>
            {WORDS.map(({ text, offset, baseDelay }) => (
              <WordRow key={text} $offset={offset} $delay={baseDelay}>
                <SplitWord word={text} baseDelay={baseDelay + 0.15} />
              </WordRow>
            ))}
          </HeadingGroup>

          <Subtitle>Plan Your Quantity and Work Your Entity</Subtitle>

          <CorporateIntro>
            MISHRAWARNA (PVT) LTD is a technology and hybrid ecosystem infrastructure
            company focused on digitally transforming traditional industries through
            scalable SaaS and hybrid operational systems.
          </CorporateIntro>

          <CTAGroup>
            <CTAButton href="#ecosystems" $primary onClick={scrollTo('ecosystems')}>
              Explore Projects →
            </CTAButton>
            <CTAButton href="#about" onClick={scrollTo('about')}>
              About Us
            </CTAButton>
          </CTAGroup>

          <StatsRow>
            <Stat>
              <div className="value">2022</div>
              <div className="label">Founded</div>
            </Stat>
            <Stat>
              <div className="value">4+</div>
              <div className="label">Years Strong</div>
            </Stat>
            <Stat>
              <div className="value">SaaS</div>
              <div className="label">Core Focus</div>
            </Stat>
          </StatsRow>
        </LeftCol>

        <RightCol>
          <BadgeOuter>
            <OrbitRing />
            <PulseRing />
            <AnniversaryBadge aria-label="4 years in operation, founded 2022">
              <Confetti />
              <BadgeTop>
                <span className="pill">Proceeding to the 5th year</span>
              </BadgeTop>
              <BigYears>
                <span className="num">5</span>
                <span className="label">Years</span>
              </BigYears>
              <BadgeTagline>Founded in 2022 — building scalable ecosystems for future</BadgeTagline>
            </AnniversaryBadge>
          </BadgeOuter>
        </RightCol>
      </HeroContent>

      <ScrollIndicator href="#ecosystems" onClick={scrollTo('ecosystems')}>
        <span>Scroll</span>
        <div className="chevron" />
      </ScrollIndicator>
    </HeroContainer>
  );
};

export default HeroSection;
